export type InsuranceQuoteProviderName = "CANADA_LIFE";

export const YES_NO_UNSET = {
  YES: "YES",
  NO: "NO",
  UNSET: "UNSET",
} as const;

export const LANGUAGES = {
  EN: "en",
  FR: "fr",
} as const;

export const UNINTERESTED_REASONS = {
  NONE: "NONE",
  EXISTING_INSURANCE_POLICY: "EXISTING_INSURANCE_POLICY",
  REFERRAL_REQUESTED: "REFERRAL_REQUESTED",
  LIMIT_REACHED: "LIMIT_REACHED",
  NOT_INTERESTED: "NOT_INTERESTED",
} as const;

export const NON_ELIGIBLE_REASONS = {
  NONE: "NONE",
  MORTGAGE_AMOUNT_TOO_LOW: "MORTGAGE_AMOUNT_TOO_LOW",
  AGE_LIMIT: "AGE_LIMIT",
  RESIDENCY_STATUS: "RESIDENCY_STATUS",
} as const;

export const INSURANCE_PRODUCT_TYPES = {
  LIFE: "LIFE",
  LIFE_DISABILITY: "LIFE_DISABILITY",
} as const;

export const INSURANCE_PRODUCT_COVERAGE_TYPES = {
  LIFE: "LIFE",
  DISABILITY: "DISABILITY",
} as const;

export const ELIGIBILITY_OPTION_TYPE = {
  WORKS_MORE_20HOURS_PER_WEEK: "WORKS_MORE_20HOURS_PER_WEEK",
  PARENTAL_LEAVE: "PARENTAL_LEAVE",
  SEASONAL_WORKER: "SEASONAL_WORKER",
  NONE: "NONE",
} as const;

export const RESIDENCY_STATUSES = {
  PERMANENT: "PERMANENT",
  TEMPORARY: "TEMPORARY",
  NON_RESIDENT: "NON_RESIDENT",
  UNKNOWN: "UNKNOWN",
} as const;

export const INSURANCE_QUOTE_STATUSES = {
  IN_PROGRESS: "IN_PROGRESS",
  PENDING_SIGNATURE: "PENDING_SIGNATURE",
  ACCEPTANCE_DECISION_REVERSED: "ACCEPTANCE_DECISION_REVERSED",
  SIGNED: "SIGNED",
  CLOSED: "CLOSED",
  SUBMITTED_TO_INSURER: "SUBMITTED_TO_INSURER",
} as const;

export const APPLICANT_STATUSES = {
  INTERESTED: "INTERESTED",
  NOT_INTERESTED: "NOT_INTERESTED",
  NOT_ELIGIBLE: "NOT_ELIGIBLE",
  ACCEPTANCE_SIGNED: "ACCEPTANCE_SIGNED",
  WAIVER_SIGNED: "WAIVER_SIGNED",
  WAIVED_AT_SIGNING: "WAIVED_AT_SIGNING",
  ACTIVATED: "ACTIVATED",
  CANCELLED: "CANCELLED",
  DECLINED_BY_INSURER: "DECLINED_BY_INSURER",
  UNSET: "UNSET",
} as const;

export const INSURANCE_PAYMENT_FREQUENCIES = {
  MONTHLY: "MONTHLY",
  WEEKLY: "WEEKLY",
  BI_WEEKLY: "BI_WEEKLY",
  SEMI_MONTHLY: "SEMI_MONTHLY",
} as const;

export const APPLICANT_FORM_CONTEXT = {
  REQUEST: "request",
  COVERAGE: "coverage",
} as const;

export type YesNoUnset = (typeof YES_NO_UNSET)[keyof typeof YES_NO_UNSET];

export type Language = (typeof LANGUAGES)[keyof typeof LANGUAGES];

export type UninterestedReason =
  (typeof UNINTERESTED_REASONS)[keyof typeof UNINTERESTED_REASONS];

export type NonEligibleReason =
  (typeof NON_ELIGIBLE_REASONS)[keyof typeof NON_ELIGIBLE_REASONS];

export type InsuranceProductType =
  (typeof INSURANCE_PRODUCT_TYPES)[keyof typeof INSURANCE_PRODUCT_TYPES];

export type EligibilityOptionType =
  (typeof ELIGIBILITY_OPTION_TYPE)[keyof typeof ELIGIBILITY_OPTION_TYPE];

export type InsuranceProductCoverageType =
  (typeof INSURANCE_PRODUCT_COVERAGE_TYPES)[keyof typeof INSURANCE_PRODUCT_COVERAGE_TYPES];

export type InsurancePaymentFrequency =
  (typeof INSURANCE_PAYMENT_FREQUENCIES)[keyof typeof INSURANCE_PAYMENT_FREQUENCIES];

export type ResidencyStatus =
  (typeof RESIDENCY_STATUSES)[keyof typeof RESIDENCY_STATUSES];

export type InsuranceQuoteStatus =
  (typeof INSURANCE_QUOTE_STATUSES)[keyof typeof INSURANCE_QUOTE_STATUSES];

export type ApplicantStatus =
  (typeof APPLICANT_STATUSES)[keyof typeof APPLICANT_STATUSES];

export type ApplicantFormContext =
  (typeof APPLICANT_FORM_CONTEXT)[keyof typeof APPLICANT_FORM_CONTEXT];

export type InsuranceApplicant = {
  applicantId: number;
  isInterested: YesNoUnset;
  language: Language;
  nonEligibleReason: NonEligibleReason;
  status: ApplicantStatus;
  uninterestedReason: UninterestedReason;
  applicantName?: string;
  applicantEmail?: string;
  products: InsuranceProduct[];
  selectedProduct?: SelectedProduct;
  isEmailValid: boolean;
};

export type InsuranceApplicantWithName = InsuranceApplicant & {
  applicantName: string;
};

export type CreditorInsuranceQuote = {
  quoteId: number;
  status: InsuranceQuoteStatus;
  providerName: InsuranceQuoteProviderName;
  createdAt: string;
  updatedAt: string;
  statusUpdatedAt: string;
};

export type QuotesInsuranceResponse = {
  results: CreditorInsuranceQuote[];
};

export type SelectedProduct = {
  insuranceProductType: InsuranceProductType;
  eligibilities?: SelectedEligibility[];
  healthQuestionAnswers: HealthQuestionAnswers[];
  isInformationComplete: boolean;
};

export type HealthQuestionAnswers = {
  questionCode: string;
  answer: YesNoUnset;
};

export type SelectedEligibility = {
  productEligibilityType: ProductEligibilityType;
  value: EligibilityOptionType;
};

export type InsuranceQuoteResponse = {
  applicationId: number;
  createdAt: string;
  externalId: string;
  id: number;
  insurancePaymentFrequency: InsurancePaymentFrequency;
  disabilityInsuredMonthlyAmount: number;
  lifeCoverageAmount: number;
  policyNumber: string;
  providerName: InsuranceQuoteProviderName;
  status: InsuranceQuoteStatus;
  updatedAt: string;
  applicants: Record<number, InsuranceApplicant>;
};

export type InsuranceProduct = {
  insuranceProductType: InsuranceProductType;
  coverages: Coverage[];
  healthQuestions: HealthQuestion[];
  eligibilities?: Eligibility[];
};

export type Eligibility = {
  productEligibilityType: ProductEligibilityType;
  options: EligibilityOption[];
};

export type EligibilityOption = {
  optionType: EligibilityOptionType;
  isEligible: boolean;
};

export type ProductEligibilityType = "WORK_STATUS";

export type HealthQuestion = {
  questionCode: string;
  question: string;
};

export type Coverage = {
  insuranceProductCoverageType: InsuranceProductCoverageType;
  premiumAmount: number;
  taxAmount: number;
};

export type GetInsuranceQuoteRequest = {
  applicationId: number;
  quoteId: number;
};

export type CreateRequestedCoverageRequest = {
  lifeCoverageAmount: number;
  disabilityInsuredMonthlyAmount: number;
  insurancePaymentFrequency: InsurancePaymentFrequency;
};

export type UpdateInsuranceApplicantRequest = {
  applicationId: number;
  quoteId: number;
  applicantId: number;
  payload: UpdateInsuranceApplicantPayload;
};

export type UpdateInsuranceApplicantPayload = {
  isInterested: YesNoUnset;
  language: Language;
  isEmailValid: boolean;
  uninterestedReason: UninterestedReason;
  selectedProduct?: SelectedProduct;
};
